import { Col, Row } from 'antd'
import { useFormikContext } from 'formik'

import { Icon } from '@iconify/react'
import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Input } from 'common/components/Input/Input'
import { InputNumber } from 'common/components/InputNumber/InputNumber'
import { Label } from 'common/components/Label/Label'
import { CodeSelect } from 'features/Procedure/features/treatments/components/code-select/code-select'
import {
  DiscountHelperText,
  DiscountSelect,
} from 'features/Procedure/features/treatments/components/discount-select/discount-select'
import type {
  DiscountTypeOption,
  EmrCodeItem,
  PatientDefaultDiscount,
} from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { LASER_KEYWORD, type FormValues } from './schema'
import { EMPTY_PROCEDURE_CODE } from './utils'

type Props = {
  isBilled?: boolean
  discountTypes?: ReadonlyArray<DiscountTypeOption>
  patientDefaultDiscount?: PatientDefaultDiscount | null
}

export const ManageEntireProcedure = ({
  isBilled = false,
  discountTypes,
  patientDefaultDiscount,
}: Props) => {
  const { values, errors, setFieldValue, handleChange } = useFormikContext<FormValues>()

  const getErrorMessage = (error: any): string | undefined => {
    if (!error) return undefined
    if (typeof error === 'string') return error
    if (error?.message) return error.message
    return undefined
  }

  const isIcdSingleSelect = Number(values.cpt4_code?.code) === EMPTY_PROCEDURE_CODE

  const isLaserProcedure = values.isLaser

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <CodeSelect
          required
          label='CPT'
          codeType={ProcedureTypeCode.CPT4}
          size='large'
          value={values.cpt4_code as EmrCodeItem | null}
          onChange={(newValue) => {
            setFieldValue('cpt4_code', newValue)
            if (newValue && 'default_price' in newValue && newValue.default_price >= 0) {
              setFieldValue('price', newValue.default_price)
            }
            const isLaser =
              !Array.isArray(newValue) &&
              (newValue?.code_text?.toLowerCase().includes(LASER_KEYWORD) ||
                newValue?.code_text_short?.toLowerCase().includes(LASER_KEYWORD) ||
                false)
            setFieldValue('isLaser', isLaser)
          }}
          placeholder='Search CPT code'
          disabled={isBilled}
          error={getErrorMessage(errors.cpt4_code)}
        />
      </Col>
      <Col xs={24}>
        <CodeSelect
          label='ICD'
          codeType={ProcedureTypeCode.ICD10}
          mode={isIcdSingleSelect ? undefined : 'multiple'}
          size='large'
          value={
            Number(values.cpt4_code?.code) === EMPTY_PROCEDURE_CODE
              ? (values.icd10_codes?.[0] as EmrCodeItem | null)
              : (values.icd10_codes as EmrCodeItem[])
          }
          onChange={(newValue) =>
            setFieldValue('icd10_codes', isIcdSingleSelect ? [newValue] : newValue)
          }
          placeholder='Search ICD codes'
          disabled={isBilled}
          error={getErrorMessage(errors.icd10_codes)}
        />
      </Col>
      <Col xs={4}>
        <Label label='Units' />
        <Input
          value={values.procedure_units}
          name='procedure_units'
          onChange={handleChange}
          placeholder='Units'
          disabled={isBilled}
          error={errors.procedure_units as string | undefined}
        />
      </Col>
      <Col xs={6}>
        <Label label='Quantity' />
        <InputNumber
          value={values.units}
          name='units'
          onChange={(stringValue) => setFieldValue('units', stringValue)}
          placeholder='Quantity'
          disabled={isBilled}
          status={errors.units ? 'error' : ''}
        />
        <ErrorLabel error={errors.units} />
      </Col>
      <Col xs={8}>
        <DiscountSelect
          value={values.discount_type_id ?? null}
          onChange={(optionId) => setFieldValue('discount_type_id', optionId)}
          formContext='treatments'
          discountTypes={discountTypes}
          patientDefaultDiscount={patientDefaultDiscount}
          disabledByBilling={isBilled}
        />
      </Col>
      <Col xs={6}>
        <Input
          label='Price'
          required
          value={values.price}
          name='price'
          onChange={handleChange}
          placeholder='Price'
          disabled={isBilled}
          prefix={<Icon style={{ marginLeft: 6 }} icon='si:dollar-fill' width={16} height={16} />}
          error={errors.price as string | undefined}
        />
      </Col>
      <Col xs={24}>
        <DiscountHelperText patientDefaultDiscount={patientDefaultDiscount} />
      </Col>
      {isLaserProcedure && (
        <>
          <Col xs={12}>
            <Input
              label='Area'
              value={values.area}
              name='area'
              onChange={handleChange}
              placeholder='Area'
              disabled={isBilled}
              error={errors.area as string | undefined}
            />
          </Col>
          <Col xs={12}>
            <Input
              label='Energy (J)'
              value={values.energy}
              name='energy'
              onChange={handleChange}
              placeholder='Energy'
              disabled={isBilled}
              error={errors.energy as string | undefined}
            />
          </Col>
          <Col xs={12}>
            <Input
              label='Pulses per section'
              value={values.pulse_per_section}
              name='pulse_per_section'
              onChange={handleChange}
              placeholder='Pulses per section'
              disabled={isBilled}
              error={errors.pulse_per_section as string | undefined}
            />
          </Col>
          <Col xs={12}>
            <Input
              label='Total Energy per Section (J)'
              value={values.total_energy}
              name='total_energy'
              onChange={handleChange}
              placeholder='Total Energy'
              disabled={isBilled}
              error={errors.total_energy as string | undefined}
            />
          </Col>
        </>
      )}
    </Row>
  )
}
